import axios from "../axiosConfig.js";
import {isNullOrUndefinedOrEmpty, aborted} from "../utils.js";
import {ENQUEUE_SNACKBAR} from "./constants.js";
import {Client, isEmpty} from "weed-js";


const client = new Client();


export const enqueueSnackbar = (notification) => {
    const key = notification.options && notification.options.key;
    return {
        type: ENQUEUE_SNACKBAR,
        notification: {
            ...notification,
            key: key || new Date().getTime() + Math.random(),
        },
    };
};

export const handleErrorResponse = (err, abortController) => {
    return dispatch => {
        const [nonFieldErrors, fieldErrors] = Client.getErrorMessage(err, abortController);

        if (!Client.wasAborted(err) && !aborted(abortController) && !isEmpty(nonFieldErrors)) {
            dispatch(enqueueSnackbar({
                message: Array.isArray(nonFieldErrors) ? nonFieldErrors.join(", ") : nonFieldErrors,
                options: {
                    variant: "error",
                },
            }));
            return Promise.reject(fieldErrors);
        }

        return Promise.reject(fieldErrors || nonFieldErrors || {detail: "Request aborted"});
    };
};

export const destroyFromDal = (uri, primary_key, delete_message, abortController) => {
    return dispatch => {
        return client.sso.dal.v1().delete(`${uri}${primary_key}/`, {signal: abortController ? abortController.signal : undefined}).then((response) => {
            if (response === undefined) {
                return dispatch(handleErrorResponse(null, abortController));
            }
            dispatch(enqueueSnackbar({
                message: delete_message !== undefined ? delete_message : "Deleted.",
                options: {
                    variant: "success",
                },
            }));
            return response.data;
        }).catch((err) => {
            return dispatch(handleErrorResponse(err, abortController));
        });
    };
};

export const getFromDal = (uri, params, abortController) => {
    const client = new Client();
    const paginate = params?.paginate;
    if (paginate !== undefined) {
        delete params.paginate;
    }
    return dispatch => dispatch(get(uri, params, client.sso.dal.v1(), abortController)).then((response) => {
        // Get list
        if (response.results) {
            if (!isEmpty(response.next) && paginate) {
                return client.sso.dal.v1().paginate(response.next, {params: {}}).then(paginatedResults => {
                    return response.results.concat(paginatedResults);
                });
            }
            return response.results;
        }
        // Get one
        return response;
    });
};

export const getFromApi = (uri, params, abortController) => {
    return dispatch => dispatch(get(uri, params, client.sso.api.v1(), abortController)).then((response) => {
        // Get list
        if (response.results) {
            if (!isEmpty(response.next)) {
                return client.sso.api.v1().paginate(response.next, {params: {}}).then(paginatedResults => {
                    return response.results.concat(paginatedResults);
                });
            }
            return response.results;
        }
        return response;
    });
};

export const get = (uri, params, using, abortController) => {
    return dispatch => {
        return using.get(uri, {
            params: params,
            signal: abortController ? abortController.signal : undefined
        }).then((response) => {
            if (response === undefined) {
                return null;
            }
            return response.data;
        }).catch((err) => {
            return dispatch(handleErrorResponse(err, abortController));
        });
    };
};

export const post = ({uri, payload, successMessage, using, abortController}) => {
    return dispatch => {
        return using.post(uri, JSON.stringify(payload), {signal: abortController ? abortController.signal : undefined}).then((response) => {
            if (response.status >= 300 || response.status < 200) {
                throw new Error("Uh oh! Looks like we're having a problem." +
                                " We're actively working on it, please try again a bit later.");
            }
            dispatch(enqueueSnackbar({
                message: isEmpty(successMessage) ? Client.getSuccessMessage(response) : successMessage,
                options: {
                    variant: "success",
                },
            }));
            return response.data;
        }).catch((err) => {
            return dispatch(handleErrorResponse(err, abortController));
        });
    };
};

export const postToDal = (uri, payload, successMessage, abortController) => {
    return dispatch => {
        return client.sso.dal.v1().post(
            uri,
            payload, {signal: abortController ? abortController.signal : undefined}).then((response) => {
            if (response.status >= 300 || response.status < 200) {
                throw new Error("Uh oh! Looks like we're having a problem." +
                                " We're actively working on it, please try again a bit later.");
            }
            dispatch(enqueueSnackbar({
                message: isEmpty(successMessage) ? Client.getSuccessMessage(response) : successMessage,
                options: {
                    variant: "success",
                    autoHideDuration: 3000
                },
            }));
            return response.data;
        }).catch((err) => {
            return dispatch(handleErrorResponse(err, abortController));
        });
    };
};

export const postToApi = (uri, payload, successMessage, abortController) => {
    return dispatch => {
        return client.sso.api.v1().post(
            uri,
            payload,
            {signal: abortController ? abortController.signal : undefined}
        ).then((response) => {
            if (response.status >= 300 || response.status < 200) {
                throw new Error(
                    "Uh oh! Looks like we're having a problem. We're" +
                    " actively working on it, please try again a bit later.");
            }
            dispatch(enqueueSnackbar({
                message: isEmpty(successMessage) ? Client.getSuccessMessage(response) : successMessage,
                options: {
                    variant: "success",
                },
            }));
            return response.data;
        }).catch((err) => {
            console.error("Got an error attempting to post to the API.");
            console.error(err);
            return dispatch(handleErrorResponse(err, abortController));
        });
    };
};

export const patchToDal = (uri, partial, successMessage, abortController) => {
    return dispatch => {
        return client.sso.dal.v1().patch(uri, partial, {signal: abortController ? abortController.signal : undefined}).then((response) => {
            if (!isNullOrUndefinedOrEmpty(response.data) && !isNullOrUndefinedOrEmpty(response.data.message)) {
                dispatch(enqueueSnackbar({
                    message: response.data.message,
                    options: {
                        variant: "success",
                    },
                }));
            } else {
                dispatch(enqueueSnackbar({
                    message: successMessage || "Updated.",
                    options: {
                        variant: "success",
                    },
                }));
            }

            return response.data;
        }).catch((err) => {
            console.error(err);
            return dispatch(handleErrorResponse(err, abortController));
        });
    };
};

export const patchToApi = (uri, partial, successMessage, abortController) => {
    return dispatch => {
        return client.sso.api.v1().patch(uri, partial, {signal: abortController ? abortController.signal : undefined}).then((response) => {
            if (!isNullOrUndefinedOrEmpty(response.data) && !isNullOrUndefinedOrEmpty(response.data.message)) {
                dispatch(enqueueSnackbar({
                    message: response.data.message,
                    options: {
                        variant: "success",
                    },
                }));
            } else {
                dispatch(enqueueSnackbar({
                    message: successMessage || "Updated.",
                    options: {
                        variant: "success",
                    },
                }));
            }
            return response.data;
        }).catch((err) => {
            return dispatch(handleErrorResponse(err, abortController));
        });
    };
};

export const createUser = (user, params, abortController) => {
    user.email = user.username;
    return dispatch => {
        const uri = "/create-user/";
        if (!isEmpty(params)) {
            const query = Object.keys(params).map(k => encodeURIComponent(k) + "=" + encodeURIComponent(params[k])).join("&");
            return dispatch(postToApi(`${uri}?${query}`, user, null, abortController));
        }
        return dispatch(postToApi("/create-user/", user, null, abortController));
    };
};

export const inviteUser = (params, abortController) => {
    return dispatch => {
        return dispatch(
            postToApi("/users/invite/",
                params,
                null,
                abortController
            ));
    };
};

export const createUserByEmail = (email, abortController) => {
    const params = {email: email, username: email, password: email};
    return dispatch => {
        return dispatch(postToApi("/create-user/", params, null, abortController));
    };
};



export const fetchAddresses = (addressId = null, abortController) => {
    const params = {};
    if (addressId) {
        params["id"] = addressId;
    }

    return dispatch => dispatch(getFromDal("/addresses/", params, abortController)).then((response => {
        if (addressId) {
            return response[0];
        }
        return response;
    })).catch(err => {
        if (!aborted(abortController)) {
            dispatch(enqueueSnackbar({
                message: "An error happened while fetching addresses.",
                options: {
                    variant: "error",
                },
            }));
        }
        return Promise.reject(err);
    });
};

export const updateAddress = (address, abortController) => {
    return dispatch => {
        return dispatch(patchToDal(`/addresses/${address.id}/`, address, "Address updated.", abortController)).catch(err => {
            dispatch(enqueueSnackbar({
                message: "An error happened while updating the address.",
                options: {
                    variant: "error",
                },
            }));
            return Promise.reject(err);
        });
    };
};

export const createAddress = (address, abortController) => {
    const addressRequest = {
        ...address,
        id: undefined,
    };
    return dispatch => {
        return dispatch(postToDal("/addresses/", addressRequest, "Address created.", abortController)).then((response) => {
            return response;
        }).catch(err => {
            dispatch(enqueueSnackbar({
                message: "An error happened while creating the address. Try again soon, please.",
                options: {
                    variant: "error",
                },
            }));
            return Promise.reject(err);
        });
    };
};

export const generatePasswordToken = (email, abortController) => {
    return dispatch => dispatch(postToApi("/accounts/forgot-password/", {
        email: email
    }, "Success. Check your email.", abortController)).then((response) => {
        return response.data;
    });
};

export const resetPassword = (password, abortController) => {
    return dispatch => dispatch(postToApi("/accounts/forgot-password/confirm/", {
        password: password,
    }, "Password changed"), abortController).then((response) => {
        return response.data;
    });
};

export const voteForIntegration = (vote, abortController) => {
    return dispatch => dispatch(
        postToDal("/integration-votes/", vote, "Thanks for your vote! If this integration is popular we'll prioritize it.", abortController)
    ).then((response) => {
        return response.data;
    });
};


export const getIntegrationVotes = (abortController) => {
    return dispatch => dispatch(
        getFromDal("/integration-votes/", {}, abortController)
    ).then((response) => {
        return response.data;
    });
};
